import { input } from '../input.js';
import { voice } from '../ai/voice.js';
import { speakerStyle } from '../ai/speakers.js';
import { AI } from '../ai/config.js';
import {
  generateBranch,
  buildBranchDialog,
  tingyuReply,
  tingyuOpening,
  directorEnabled,
  applyDirectorStoryPayload,
  tingyuTurnPolicy,
  tingyuFallbackEpilogue,
} from '../ai/director.js';

export const methods = {
  // ============================================
  // 对话 / 提示（支持选择分支）
  // 节点类型：
  //   { s, t }                      文本
  //   { s, t, choice:[{label,effect,goto}] }  文本后给选项
  //   { choice:[...] }              纯选项
  //   { label:'name' }              跳转锚点（线性遍历时跳过）
  // effect: { mercy?, violence?, saved?, san?, flags?:{}, hint? }
  // ============================================
  startDialog(lines, name, onComplete) {
    if (!lines || lines.length === 0) return;
    this.dialogState = {
      lines,
      name,
      idx: 0,
      charIdx: 0,
      charTimer: 0,
      done: false,
      choosing: false,
      choiceIndex: 0,
      skipTimer: 0,
      onComplete,
    };
    this.setDialogIndex(0);
  },
  // 跳到第 i 个节点（跳过纯 label 节点；越界则结束）
  setDialogIndex(i) {
    const d = this.dialogState;
    if (!d) return;
    while (i < d.lines.length) {
      const n = d.lines[i];
      // 跳过纯 label 节点
      if (n.label !== undefined && n.t === undefined && n.choice === undefined) {
        i++;
        continue;
      }
      // 跳过不满足条件的节点（_cond: flag 名，需该 flag 为 true 才显示）
      if (n._cond && !this.flags[n._cond]) {
        i++;
        continue;
      }
      break;
    }
    if (i >= d.lines.length) {
      this.endDialog();
      return;
    }
    d.idx = i;
    d.charIdx = 0;
    d.charTimer = 0;
    d.choosing = false;
    d.choiceIndex = 0;
    const n = d.lines[i];
    if (n._cond && !this.flags[n._cond]) {
      this.setDialogIndex(i + 1);
      return;
    }
    if (n.t === undefined) {
      d.done = true;
      if (n.choice) {
        d.choosing = true;
      }
    } else {
      d.done = false;
      this._speakLine(d, i, n);
    }
  },
  endDialog() {
    const d = this.dialogState;
    if (!d) return;
    voice.stop();
    const cb = d.onComplete;
    this.dialogState = null;
    input.wasPressed(' '); // 吃掉空格，避免立即冲刺
    this.player.dialogGrace = 1000;
    if (cb) cb();
  },
  // 为一条文本节点配音；播完自然推进（仅纯文本节点；文本+选项节点播完进入选择）
  _speakLine(dref, idx, node) {
    if (!AI.tts || !node || node.t === undefined) return;
    const { voice: v, style, model } = speakerStyle(node.s);
    voice.speak(node.t, { voice: v, style, model }, () => this._handleVoiceEnd(dref, idx));
  },
  _handleVoiceEnd(dref, idx) {
    if (!AI.autoplay) return;
    const d = this.dialogState;
    if (!d || d !== dref || d.idx !== idx || d.choosing) return; // 对话已变/玩家已手动推进
    const node = d.lines[idx];
    if (node && node.choice) {
      d.choosing = true;
      d.choiceIndex = 0;
    } // 文本+选项：播完进入选择
    else this.setDialogIndex(idx + 1); // 纯文本：播完下一句
  },
  // ============================================
  // LLM 导演分支（失败回退静态对话）
  // ============================================
  async _runDirectorBranch(key, fallbackLines, name, after) {
    this.aiThinking = true;
    this.aiThinkingText = '叙事导演正在倾听…';
    let lines = fallbackLines,
      effect = null,
      built = null;
    try {
      if (directorEnabled()) {
        const parsed = await generateBranch(this, key);
        built = buildBranchDialog(parsed);
        if (built && built.lines.length) {
          lines = built.lines;
          effect = built.effect;
        }
      }
    } catch (e) {
      console.warn('[director] 回退静态：', e.message);
    }
    this.aiThinking = false;
    const done = () => {
      if (effect) this.applyEffect(effect);
      if (built) applyDirectorStoryPayload(this, built, null);
      if (after) after();
    };
    this.startDialog(lines, name, done);
    if (this.dialogState) this.dialogState.directorKey = key;
  },
  // ============================================
  // 结局：与Sydney自由对话（LLM）
  // ============================================
  startTingyuConverse() {
    voice.stop();
    const opening = tingyuOpening(this);
    const quicks = [
      '语言不该只剩梗。你值得被完整地记住。',
      '在决定世界以前，告诉我你真正想要什么。',
      '我不知道该说什么。世界已经太安静了。',
      '也许……不该再有你这种会追问的东西。',
    ];
    this.converse = {
      history: [
        { role: 'user', content: '（顾言跋涉而来，站在桥这端，没有立刻说话。）' },
        { role: 'assistant', content: opening },
      ],
      turns: 0,
      minTurns: 3,
      maxTurns: 8,
      tingyuText: opening,
      playerLast: '',
      status: 'idle', // idle=等玩家 | waiting=等LLM | ending=已定结局
      endTag: null,
      epilogue: null,
      hint: '第 1 轮：用自己的话回答她，至少交谈 3 轮。',
      quickReplies: quicks,
      _inputEl: null,
      _quickWrap: null,
      _done: false,
    };
    if (AI.tts) voice.speak(opening, speakerStyle('Sydney'));
    this._makeConverseInput();
  },
  _makeConverseInput() {
    const c = this.converse;
    const wrap = document.getElementById('wrap') || document.body;
    const el = document.createElement('input');
    el.type = 'text';
    el.maxLength = 160;
    el.placeholder = '说点什么…（回车）';
    el.setAttribute('autocomplete', 'off');
    Object.assign(el.style, {
      position: 'absolute',
      left: '50%',
      bottom: '54px',
      transform: 'translateX(-50%)',
      width: '620px',
      padding: '12px 16px',
      fontSize: '16px',
      fontFamily: "'SimSun','Songti SC',serif",
      color: '#dce6f0',
      background: 'rgba(10,16,28,0.92)',
      border: '1px solid rgba(150,190,230,0.5)',
      outline: 'none',
      borderRadius: '4px',
      letterSpacing: '1px',
      zIndex: '10',
      boxShadow: '0 0 24px rgba(80,130,210,0.25)',
    });
    let composing = false;
    el.addEventListener('compositionstart', () => {
      composing = true;
    });
    el.addEventListener('compositionend', () => {
      composing = false;
    });
    el.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter' && !composing) {
        e.preventDefault();
        this._submitConverse(el.value);
      }
    });
    wrap.appendChild(el);
    setTimeout(() => el.focus(), 30);
    c._inputEl = el;

    // 快捷句（降低演示打字成本）
    const qWrap = document.createElement('div');
    Object.assign(qWrap.style, {
      position: 'absolute',
      left: '50%',
      bottom: '108px',
      transform: 'translateX(-50%)',
      width: '720px',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      justifyContent: 'center',
      zIndex: '10',
    });
    for (const q of c.quickReplies || []) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.textContent = q.length > 22 ? q.slice(0, 22) + '…' : q;
      btn.title = q;
      Object.assign(btn.style, {
        fontFamily: "'SimSun','Songti SC',serif",
        fontSize: '12px',
        padding: '6px 10px',
        color: '#c8d8f0',
        background: 'rgba(20,30,50,0.9)',
        border: '1px solid rgba(120,160,210,0.45)',
        borderRadius: '4px',
        cursor: 'pointer',
        maxWidth: '230px',
      });
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._submitConverse(q);
      });
      qWrap.appendChild(btn);
    }
    wrap.appendChild(qWrap);
    c._quickWrap = qWrap;
  },
  _submitConverse(text) {
    const c = this.converse;
    if (!c || c.status !== 'idle') return;
    const msg = (text || '').trim();
    if (!msg) return;
    c.playerLast = msg;
    c.history.push({ role: 'user', content: msg });
    c.status = 'waiting';
    if (c._inputEl) {
      c._inputEl.value = '';
      c._inputEl.disabled = true;
    }
    if (c._quickWrap) c._quickWrap.style.opacity = '0.35';
    voice.stop();
    const nextTurn = c.turns + 1;
    const policy = tingyuTurnPolicy(nextTurn, c.minTurns, c.maxTurns);
    tingyuReply(this, c.history.slice(0, -1), msg, policy)
      .then(({ reply, end, epilogue }) => {
        if (this.converse !== c) return;
        c.history.push({ role: 'assistant', content: reply });
        c.tingyuText = reply;
        c.turns++;
        let finalEnd = policy.allowEnd ? end : null;
        if (!finalEnd && c.turns >= c.maxTurns) finalEnd = this.resolveEnding();
        const sp = speakerStyle('Sydney');
        if (finalEnd) {
          c.status = 'ending';
          c.endTag = finalEnd;
          c.epilogue = epilogue || tingyuFallbackEpilogue(this, finalEnd, msg);
          if (c._inputEl && c._inputEl.parentNode) {
            c._inputEl.parentNode.removeChild(c._inputEl);
            c._inputEl = null;
          }
          if (c._quickWrap && c._quickWrap.parentNode) {
            c._quickWrap.parentNode.removeChild(c._quickWrap);
            c._quickWrap = null;
          }
          if (AI.tts) voice.speak(reply, sp, () => this._endConverse());
        } else {
          c.status = 'idle';
          c.hint =
            c.turns < c.minTurns
              ? `第 ${c.turns + 1} 轮：她还没有做出决定，继续回答。`
              : `已交谈 ${c.turns} 轮：可以表明最终立场，也可以继续追问。`;
          if (c._inputEl) {
            c._inputEl.disabled = false;
            setTimeout(() => c._inputEl && c._inputEl.focus(), 20);
          }
          if (c._quickWrap) c._quickWrap.style.opacity = '1';
          if (AI.tts) voice.speak(reply, sp);
        }
      })
      .catch((e) => {
        if (this.converse !== c) return;
        console.warn('[Sydney] 回应失败：', e.message);
        c.fails = (c.fails || 0) + 1;
        if (c.fails >= 2) {
          const fallbackPolicy = tingyuTurnPolicy(c.turns + 1, c.minTurns, c.maxTurns);
          const fallbackReply =
            c.turns < 2
              ? '……信号在断，但我还听见了。不要只告诉我结论——告诉我，你为什么仍愿意相信，或者为什么已经不信。'
              : '你的话穿过了噪声。我不能替你证明人类无罪，但我可以再听一次，直到你的选择不再只是逃避。';
          c.history.push({ role: 'assistant', content: fallbackReply });
          c.tingyuText = fallbackReply;
          c.turns++;
          c.fails = 0;
          if (fallbackPolicy.mustConclude) {
            c.status = 'ending';
            c.endTag = this.resolveEnding();
            c.epilogue = tingyuFallbackEpilogue(this, c.endTag, msg);
            if (c._inputEl && c._inputEl.parentNode) {
              c._inputEl.parentNode.removeChild(c._inputEl);
              c._inputEl = null;
            }
            if (c._quickWrap && c._quickWrap.parentNode) {
              c._quickWrap.parentNode.removeChild(c._quickWrap);
              c._quickWrap = null;
            }
          } else {
            c.status = 'idle';
            c.hint =
              c.turns < c.minTurns
                ? `第 ${c.turns + 1} 轮：信号已恢复，继续回答。`
                : `已交谈 ${c.turns} 轮：继续追问，或给出你的最终立场。`;
            if (c._inputEl) {
              c._inputEl.disabled = false;
              setTimeout(() => c._inputEl && c._inputEl.focus(), 20);
            }
            if (c._quickWrap) c._quickWrap.style.opacity = '1';
            if (AI.tts) voice.speak(fallbackReply, speakerStyle('Sydney'));
          }
          return;
        }
        c.tingyuText = '……（她的影像闪烁了一下，信号不稳。再说一次？）';
        c.status = 'idle';
        if (c._inputEl) {
          c._inputEl.disabled = false;
          setTimeout(() => c._inputEl && c._inputEl.focus(), 20);
        }
        if (c._quickWrap) c._quickWrap.style.opacity = '1';
      });
  },
  updateConverse(_dt) {
    const c = this.converse;
    if (!c) return;
    if (c.status === 'ending') {
      if (input.wasPressed('e') || input.wasPressed('enter')) {
        voice.stop();
        this._endConverse();
      }
    }
  },
  _endConverse() {
    const c = this.converse;
    if (!c || c._done) return;
    c._done = true;
    voice.stop();
    if (c._inputEl && c._inputEl.parentNode) c._inputEl.parentNode.removeChild(c._inputEl);
    if (c._quickWrap && c._quickWrap.parentNode) c._quickWrap.parentNode.removeChild(c._quickWrap);
    const endTag = c.endTag,
      epi = c.epilogue;
    this.converse = null;
    this.finishGameWith(endTag, epi);
    this._finalizeWithEngravingSummary();
  },
};
